import React, { Component } from 'react'
import { getValueCookie } from '../../services/cookieService';
import { getInformationWithDataV3 } from '../../services/CABE';
import "../../css/general-style.css"
import '../../css/table-responsive.css'
import "../../css/dashboard.css"
import { pricesFormat } from '../../functions/pricesFormat';
import { NumberFormat } from '../../functions/generalFunctions';
import ExcelDocument from '../../components/ExcelDocument';
import { getDateFromReports } from '../../functions/dateFormat';
import { ReportPriceMarginV3 } from '../../functions/generateDataSetExcel';
import { AiOutlineReload, AiOutlineBarChart, AiOutlineSearch, AiOutlineClose, AiFillFileExcel, AiOutlineWarning } from "react-icons/ai";

// Menu
import { Menu } from "../../components/Menu/Menu";

const LEVELS = [1, 2, 3, 4]
const PAGE_SIZE = 10          // product LINES per page, not items (backend max 50)
const EXPORT_PAGE_SIZE = 50
const SEARCH_DEBOUNCE_MS = 500
const MARGIN_OK = 20          // same green/amber threshold the page always used
// Prices (cost + levels) are the key figures, one step above the table's
// 0.875rem base; margins sit one step below them, still readable on tablets.
const PRICE_FONT = '0.9375rem'
const MARGIN_FONT = '0.8125rem'
const EXPORT_BUTTON_ID = 'priceMarginsV3ExcelDownload'

// Backend sends prices/margins already computed — this page never does math,
// only formats. null always means "missing", never 0.
const showPrice = (v) => (v === null || v === undefined ? null : `$${NumberFormat(pricesFormat(v))}`)
const showMargin = (v) => (v === null || v === undefined ? null : `${Number(v).toFixed(2)}%`)

// Missing-price validation (2026-10-08, see the backend handoff for
// /v3/items/margin): error = no product_pricing row at all, warning = row
// exists but cost and/or some level price is missing.
function getPricingIssues(product) {
    if (!product.hasPricing || !product.pricing) {
        return { severity: 'error', messages: ['No prices in Price Management'] }
    }
    const p = product.pricing
    const messages = []
    if (p.cost === null) messages.push('No cost')
    const missingLevels = LEVELS.filter((n) => p[`level${n}Price`] === null)
    if (missingLevels.length) messages.push(`Missing levels: ${missingLevels.join(', ')}`)
    return { severity: messages.length ? 'warning' : 'ok', messages }
}

const withIssues = (products) => products.map((product) => ({ ...product, issues: getPricingIssues(product) }))

/**
 * MargenesPrecioV3 — /price_margins_v3.
 *
 * 2026-08-14: cloned from MargenesPreciov2.js to fix the excelContainer
 * classList crash (imperative DOM calls after an async setState).
 *
 * 2026-10-08: moved to /v3/items/margin. Prices now come from
 * product_pricing (the same ones Price Management, SO V3 and Invoice V3
 * use) instead of MAS90's IM_PriceCode over the WebSocket, and every price
 * and margin arrives already computed — the old per-level markup math and
 * the Altitude-specific branch are gone. Paginated server-side by product
 * line (a line never splits across pages), so search/line filter also run
 * server-side. Items with missing prices are flagged in red/amber so the
 * admin can go complete them in Price Management.
 */
export default class MargenesPrecioV3 extends Component {
    state = {
        idCompany: Number(getValueCookie('CompanyId')),
        loading: false,
        loadError: '',
        products: [],       // current page, each with `issues` attached
        lines: [],          // product lines on the current page
        total: 0,           // total product LINES matching the filters
        page: 1,
        search: '',
        productLine: null,  // { code, desc } when filtered to one line
        onlyMissing: false,
        exporting: false,
        exportError: '',
        exportRows: [],
    }

    searchTimer = null
    loadSeq = 0
    unmounted = false

    componentDidMount() {
        this.loadPage(1)
    }

    componentWillUnmount() {
        this.unmounted = true
        clearTimeout(this.searchTimer)
    }

    requestPage(page, pageSize) {
        const { idCompany, search, productLine } = this.state
        return getInformationWithDataV3('/v3/items/margin', {
            idCompany,
            search: search.trim(),
            productLine: productLine ? productLine.code : '',
            page,
            pageSize,
        })
    }

    loadPage = async (page) => {
        const seq = ++this.loadSeq
        this.setState({ loading: true, loadError: '', page })
        const resp = await this.requestPage(page, PAGE_SIZE)
        // Drop the response if a newer search/page request already started.
        if (this.unmounted || seq !== this.loadSeq) return

        if (!resp || !resp.status || resp.status.code !== 1 || !resp.data) {
            const detail = resp && (resp.message || resp.error)
            this.setState({ loading: false, products: [], lines: [], total: 0, loadError: `Could not load price margins${detail ? ` (${detail})` : ''}. Please try again.` })
            return
        }
        this.setState({
            loading: false,
            products: withIssues(Array.isArray(resp.data.products) ? resp.data.products : []),
            lines: Array.isArray(resp.data.lines) ? resp.data.lines : [],
            total: Number(resp.total) || 0,
        })
    }

    onSearchChange = (e) => {
        clearTimeout(this.searchTimer)
        this.setState({ search: e.target.value })
        this.searchTimer = setTimeout(() => this.loadPage(1), SEARCH_DEBOUNCE_MS)
    }

    searchNow = () => {
        clearTimeout(this.searchTimer)
        this.loadPage(1)
    }

    filterByLine = (line) => {
        this.setState({ productLine: { code: line.ProductLine, desc: line.ProductLineDesc || line.ProductLine } }, () => this.loadPage(1))
    }

    clearLine = () => {
        this.setState({ productLine: null }, () => this.loadPage(1))
    }

    // Export walks every page with the current search/line filter (the table
    // only holds one page), then clicks the hidden react-data-export button —
    // it can only download data that's already rendered.
    exportExcel = async () => {
        this.setState({ exporting: true, exportError: '' })
        const rows = []
        let page = 1
        let totalPages = 1
        do {
            const resp = await this.requestPage(page, EXPORT_PAGE_SIZE)
            if (this.unmounted) return
            if (!resp || !resp.status || resp.status.code !== 1 || !resp.data) {
                this.setState({ exporting: false, exportError: 'Could not export — the report did not finish loading. Please try again.' })
                return
            }
            rows.push(...withIssues(resp.data.products || []))
            totalPages = Math.max(1, Math.ceil((Number(resp.total) || 0) / EXPORT_PAGE_SIZE))
            page++
        } while (page <= totalPages)

        const exportRows = (this.state.onlyMissing ? rows.filter((r) => r.issues.severity !== 'ok') : rows)
            .map((r) => ({ ...r, statusText: r.issues.messages.join(' · ') }))

        this.setState({ exporting: false, exportRows }, () => {
            const button = document.getElementById(EXPORT_BUTTON_ID)
            if (button) button.click()
        })
    }

    renderLevelCell(pricing, n, rowHasPricing) {
        const price = pricing ? showPrice(pricing[`level${n}Price`]) : null
        const marginValue = pricing ? pricing[`level${n}Margin`] : null
        const margin = showMargin(marginValue)

        if (!price) {
            return (
                <td key={n} className="text-center">
                    {rowHasPricing
                        ? <span style={{ color: '#B45309', fontSize: MARGIN_FONT, fontWeight: 600 }}>Missing</span>
                        : <span style={{ color: '#94A3B8' }}>—</span>}
                </td>
            )
        }

        const tone = marginValue === null
            ? { bg: '#F1F5F9', fg: '#475569' }
            : marginValue < 0
                ? { bg: 'rgba(220,38,38,0.1)', fg: '#B91C1C' }
                : marginValue >= MARGIN_OK
                    ? { bg: 'rgba(16,185,129,0.1)', fg: '#047857' }
                    : { bg: 'rgba(245,158,11,0.12)', fg: '#B45309' }

        return (
            <td key={n} className="text-end" style={{ whiteSpace: 'nowrap' }}>
                <div style={{ fontWeight: 600, fontSize: PRICE_FONT, color: '#0F172A' }}>{price}</div>
                <span style={{ display: 'inline-block', marginTop: '2px', padding: '2px 9px', borderRadius: '999px', fontSize: MARGIN_FONT, fontWeight: 600, background: tone.bg, color: tone.fg }}>
                    {margin || 'no margin'}
                </span>
            </td>
        )
    }

    renderStatus(issues) {
        if (issues.severity === 'ok') {
            return <span style={{ color: '#047857', fontSize: '0.78rem', fontWeight: 600 }}>OK</span>
        }
        const isError = issues.severity === 'error'
        return (
            <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '6px',
                fontSize: '0.76rem', fontWeight: 700, whiteSpace: 'nowrap',
                background: isError ? '#FEE2E2' : '#FEF3C7', color: isError ? '#991B1B' : '#92400E',
                border: `1px solid ${isError ? '#FECACA' : '#FDE68A'}`,
            }}>
                <AiOutlineWarning /> {issues.messages.join(' · ')}
            </span>
        )
    }

    render() {
        const { loading, loadError, products, lines, total, page, search, productLine, onlyMissing, exporting, exportError, exportRows } = this.state
        const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
        const errorCount = products.filter((p) => p.issues.severity === 'error').length
        const warningCount = products.filter((p) => p.issues.severity === 'warning').length
        const visible = onlyMissing ? products.filter((p) => p.issues.severity !== 'ok') : products
        const columnCount = 4 + LEVELS.length

        return (
            <div className="dash-page pb-5">
                <Menu Refresh={this.state.Refresh} Father={this} />

                {/* ── Hero ────────────────────────────────────────────── */}
                <div style={{ marginTop: 30, background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #2563EB 100%)', padding: '2rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: '12px', padding: '0.75rem', display: 'flex' }}>
                            <AiOutlineBarChart style={{ color: 'white', fontSize: '1.75rem' }} />
                        </div>
                        <div>
                            <h1 style={{ color: 'white', fontWeight: 800, fontSize: '1.375rem', margin: 0 }}>Price Margins (V3)</h1>
                            <p style={{ color: 'rgba(255,255,255,0.75)', margin: 0, fontSize: '0.8rem' }}>
                                Prices and margins from Price Management · {getValueCookie('Company')}
                            </p>
                        </div>
                    </div>
                    <button type="button" onClick={() => this.loadPage(page)} disabled={loading}
                        style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '10px', color: 'white', padding: '0.6rem 1.1rem', fontWeight: 600, fontSize: '0.875rem', cursor: loading ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <AiOutlineReload /> Reload
                    </button>
                </div>

                <div className="container-fluid" style={{ marginTop: 16 }}>
                    {/* ── Toolbar ─────────────────────────────────────────── */}
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: '520px' }}>
                            <AiOutlineSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }} />
                            <input type="text" autoComplete="off" aria-label="Search by item code or description"
                                placeholder="Search by item code or description…" value={search}
                                onChange={this.onSearchChange}
                                onKeyDown={(e) => { if (e.key === 'Enter') this.searchNow() }}
                                style={{ width: '100%', padding: '0.7rem 1rem 0.7rem 2.2rem', border: '1.5px solid #E2E8F0', borderRadius: '10px', fontSize: '0.9375rem' }} />
                        </div>

                        {productLine && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(37,99,235,0.08)', color: '#1e40af', border: '1px solid #BFDBFE', borderRadius: '999px', padding: '4px 6px 4px 12px', fontSize: '0.8rem', fontWeight: 700 }}>
                                Line: {productLine.desc}
                                <button type="button" onClick={this.clearLine} aria-label="Clear product line filter"
                                    style={{ border: 'none', background: 'transparent', color: '#1e40af', display: 'flex', padding: '2px', cursor: 'pointer' }}>
                                    <AiOutlineClose />
                                </button>
                            </span>
                        )}

                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', fontWeight: 600, color: '#334155', cursor: 'pointer', userSelect: 'none' }}>
                            <input type="checkbox" className="form-check-input" style={{ margin: 0 }} checked={onlyMissing}
                                onChange={(e) => this.setState({ onlyMissing: e.target.checked })} />
                            Only items with missing prices
                        </label>

                        <div style={{ marginLeft: 'auto' }}>
                            <button type="button" className="btn greenButton" onClick={this.exportExcel} disabled={exporting || loading || total === 0}
                                style={{ padding: '0.6rem 1rem', fontSize: '0.8375rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                {exporting
                                    ? <><span className="spinner-border spinner-border-sm" aria-hidden="true"></span> Preparing...</>
                                    : <><AiFillFileExcel /> Download Report</>}
                            </button>
                            {/* Hidden — exportExcel() fills exportRows and clicks it. */}
                            <div hidden>
                                <ExcelDocument id={EXPORT_BUTTON_ID} hidden
                                    archname={'Price Margins ' + getValueCookie('Company') + ' ' + getDateFromReports()}
                                    sheetname={'Price Margins'}
                                    data={ReportPriceMarginV3(exportRows)} />
                            </div>
                        </div>
                    </div>

                    {/* ── Summary + legend ────────────────────────────────── */}
                    {!loading && !loadError && products.length > 0 && (
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.75rem', fontSize: '0.8rem' }}>
                            <span style={{ color: '#475569' }}>On this page: <strong>{products.length}</strong> items</span>
                            <span className="margin-legend margin-legend-error">{errorCount} with no prices</span>
                            <span className="margin-legend margin-legend-warning">{warningCount} incomplete (cost or levels)</span>
                            {(errorCount + warningCount) > 0 && (
                                <span style={{ color: '#64748B' }}>— complete them in Price Management.</span>
                            )}
                        </div>
                    )}

                    {(loadError || exportError) && (
                        <div role="alert" style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B', borderRadius: '8px', padding: '0.75rem 1rem', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                            {loadError || exportError}
                        </div>
                    )}

                    {/* ── Table ───────────────────────────────────────────── */}
                    <div className="tableFixHeadSmall" style={{ maxHeight: '70vh', opacity: loading ? 0.55 : 1, transition: 'opacity 0.15s' }}>
                        <table className="table table-clean align-middle">
                            <thead>
                                <tr>
                                    <th>Item Code</th>
                                    <th>Description</th>
                                    <th className="text-end">Cost</th>
                                    {LEVELS.map((n) => (
                                        <th key={n} className={`text-end ${n % 2 === 1 ? 'table-blue-header' : 'table-aqua-header'}`}>Level {n}</th>
                                    ))}
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lines.map((line) => {
                                    const lineProducts = visible.filter((p) => p.productLine === line.ProductLine)
                                    if (onlyMissing && lineProducts.length === 0) return null
                                    const lineIssues = products.filter((p) => p.productLine === line.ProductLine && p.issues.severity !== 'ok').length
                                    return (
                                        <React.Fragment key={line.ProductLine}>
                                            <tr className="margin-line-row">
                                                <td colSpan={columnCount}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                                                        <strong>{line.ProductLineDesc || line.ProductLine}</strong>
                                                        <span style={{ color: '#64748B', fontSize: '0.78rem' }}>{line.ProductLine} · {line.itemCount} items</span>
                                                        {lineIssues > 0 && (
                                                            <span style={{ color: '#B45309', fontSize: '0.78rem', fontWeight: 700 }}>{lineIssues} with missing prices</span>
                                                        )}
                                                        {!productLine && (
                                                            <button type="button" onClick={() => this.filterByLine(line)}
                                                                style={{ marginLeft: 'auto', border: '1px solid #CBD5E1', background: '#FFFFFF', color: '#1e40af', borderRadius: '6px', padding: '2px 10px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>
                                                                Show only this line
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                            {lineProducts.map((product) => {
                                                const p = product.pricing
                                                const rowClass = product.issues.severity === 'error' ? 'margin-row-error' : product.issues.severity === 'warning' ? 'margin-row-warning' : ''
                                                return (
                                                    <tr key={product.itemCode} className={rowClass}>
                                                        <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{product.itemCode}</td>
                                                        <td>{product.description}</td>
                                                        <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                                                            {p && showPrice(p.cost)
                                                                ? <strong style={{ fontSize: PRICE_FONT }}>{showPrice(p.cost)}</strong>
                                                                : <span style={{ color: p ? '#B45309' : '#94A3B8', fontWeight: 600, fontSize: p ? MARGIN_FONT : undefined }}>{p ? 'Missing' : '—'}</span>}
                                                        </td>
                                                        {LEVELS.map((n) => this.renderLevelCell(p, n, !!p))}
                                                        <td>{this.renderStatus(product.issues)}</td>
                                                    </tr>
                                                )
                                            })}
                                        </React.Fragment>
                                    )
                                })}

                                {!loading && !loadError && products.length === 0 && (
                                    <tr><td colSpan={columnCount} style={{ textAlign: 'center', padding: '3rem 1rem', color: '#64748B' }}>No items match your search.</td></tr>
                                )}
                                {!loading && onlyMissing && products.length > 0 && visible.length === 0 && (
                                    <tr><td colSpan={columnCount} style={{ textAlign: 'center', padding: '2rem 1rem', color: '#047857' }}>Every item on this page has complete prices.</td></tr>
                                )}
                                {loading && products.length === 0 && (
                                    <tr><td colSpan={columnCount} style={{ textAlign: 'center', padding: '3rem 1rem', color: '#64748B' }}>
                                        <span className="spinner-border spinner-border-sm" aria-hidden="true"></span> Loading price margins...
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* ── Pagination (by product line) ────────────────────── */}
                    {total > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.75rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                            <button type="button" className="btn blueButton" disabled={loading || page <= 1} onClick={() => this.loadPage(page - 1)}>Prev</button>
                            <span style={{ fontSize: '0.85rem', color: '#475569' }}>Page {page} of {totalPages} · {total} product lines</span>
                            <button type="button" className="btn blueButton" disabled={loading || page >= totalPages} onClick={() => this.loadPage(page + 1)}>Next</button>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}
